import React, { useEffect, useState } from 'react';
import { Grid, Box, Card, CardContent, Typography, List, ListItem, ListItemText, useMediaQuery } from '@mui/material';
import { LinkedIn, PhoneAndroid, Circle, CircleOutlined, WorkspacePremiumOutlined } from '@mui/icons-material';
import { useFetchProfileQuery } from '../profileApiSlice';
import { useParams } from 'react-router-dom';
import { variants } from '../../../variants/variants';
import { RenderProfileDetail, RenderCard, SkillRenderCard, ProfileCard } from './ProfileComponents';
import Skype from '../../../assets/Skype';
import ProfileSkeleton from './ProfileSkeleton';
import { useTheme } from '@emotion/react';

const Profile = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const { empCode } = useParams();
  const { data, isLoading, error } = useFetchProfileQuery(empCode);
  const [showAll, setShowAll] = useState({
    skills: false,
    certifications: false,
    projects: false,
    education: false,
    workHistory: false,
  });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
  }, [empCode]);

  useEffect(() => {
    if (data !== undefined) {
      setLoading(false);
    }
  }, [data]);

  if (loading || isLoading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return <div>Error loading profile</div>;
  }

  if (error) return <div>Error loading profile</div>;
  const user = data ? data : null;
  if (!user) return <div>User not found</div>;

  const handleShowAllToggle = key => {
    setShowAll(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const formatDateString = dateString => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  const ProficiencyDots = ({ proficiency }) => {
    const filledDots = proficiency === 'Advanced' ? 5 : proficiency === 'Intermediate' ? 3 : 2;
    const totalDots = 5;

    return (
      <Box>
        {Array.from({ length: filledDots }).map((_, index) => (
          <Circle fontSize='12px' key={`filled-${index}`} color='primary' />
        ))}
        {Array.from({ length: totalDots - filledDots }).map((_, index) => (
          <CircleOutlined fontSize='12px' key={`unfilled-${index}`} color='primary' />
        ))}
      </Box>
    );
  };

  return (
    <div className='content' style={{ padding: 0 }}>
      <Box
        sx={{
          backgroundColor: isSm? '' : '#EEF5FF',
          height: '100%',
          overflow: 'hidden',
          borderRadius: variants.boxRadius.NAVBAR,
          border: isSm? 0 : 1,
          borderColor: 'divider',
        }}
      >
        <Grid container spacing={2} sx={{ overflow: 'auto', height: '92vh', padding: isSm? 0 : 1}}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{ borderRadius: '8px', boxShadow: 'none', border: 1, borderColor: 'divider', minHeight: '42.5vh' }}
            >
              <CardContent>
                <Typography variant='h6' fontWeight='bold'>
                  About
                </Typography>
                <ProfileCard user={user} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              sx={{ borderRadius: '8px', boxShadow: 'none', border: 1, borderColor: 'divider', maxHeight: '22.5vh' }}
            >
              <CardContent>
                <Typography variant='h6' fontWeight='bold'>
                  Social
                </Typography>
                <RenderProfileDetail
                  icon={<PhoneAndroid />}
                  label='Phone:'
                  value={user?.contact && user.contact[0]?.phoneNumber}
                />
                <RenderProfileDetail
                  icon={<LinkedIn />}
                  label='LinkedIn'
                  value={
                    <a
                      href={user?.contact && user?.contact[0]?.linkedinUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{ textDecoration: 'none' }}
                    >
                      {user?.contact && user?.contact[0]?.linkedinUrl ? user?.name : 'N/A'}
                    </a>
                  }
                />

                <RenderProfileDetail
                  icon={<Skype />}
                  label='Skype:'
                  value={user?.contact && user?.contact[0]?.skypeId ? user?.contact[0]?.skypeId : 'N/A'}
                />
              </CardContent>
            </Card>
            <Card
              sx={{
                mt: 2,
                borderRadius: '8px',
                boxShadow: 'none',
                border: 1,
                borderColor: 'divider',
                height: '21vh',
              }}
            >
              <CardContent>
                <Typography variant='h6' fontWeight='bold'>
                  Languages
                </Typography>
                <List sx={{ overflow: 'auto', height: '14vh' }}>
                  {user?.languages?.map((language, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemText primary={language?.language} secondary={language?.proficiency} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <SkillRenderCard
              title='Skills'
              items={user?.skills}
              showAll={showAll}
              showAllKey='skills'
              sliceCount={4}
              renderItem={skill => (
                <ListItem sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Typography>{skill.category}</Typography>
                  <ProficiencyDots proficiency={skill.skills.proficiency} />
                </ListItem>
              )}
              handleShowAllToggle={handleShowAllToggle}
            />
          </Grid>
          <RenderCard
            title='Certifications'
            items={user?.certifications}
            showAll={showAll}
            showAllKey='certifications'
            sliceCount={4}
            renderItem={certification => (
              <Grid item xs={12}>
                <Card variant='outlined'>
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <WorkspacePremiumOutlined sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant='body1'>{certification}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
            handleShowAllToggle={handleShowAllToggle}
          />
          <RenderCard
            title='Projects'
            items={user?.projects}
            showAll={showAll}
            showAllKey='projects'
            sliceCount={1}
            renderItem={project => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {project.domain}
                </Typography>
                <Typography variant='body2'>{`Description: ${project.description}`}</Typography>
                <Typography variant='body2'>{`Technologies: ${project.technologies}`}</Typography>
                <Typography variant='body2'>{`Duration: ${formatDateString(project.startDate)} to ${formatDateString(
                  project.endDate
                )}`}</Typography>
              </Box>
            )}
            handleShowAllToggle={handleShowAllToggle}
          />
          <RenderCard
            title='Education'
            items={user?.education}
            showAll={showAll}
            showAllKey='education'
            sliceCount={2}
            renderItem={edu => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {edu.institution}
                </Typography>
                <Typography variant='body2'>{`Degree: ${edu.degree}`}</Typography>
                <Typography variant='body2'>{`Field of study: ${edu.fieldOfStudy}`}</Typography>
                <Typography variant='body2'>{`Duration: ${formatDateString(edu.startDate)} to ${formatDateString(
                  edu.endDate
                )}`}</Typography>
              </Box>
            )}
            handleShowAllToggle={handleShowAllToggle}
          />
          <RenderCard
            title='Work History'
            items={user?.workHistory}
            showAll={showAll}
            showAllKey='workHistory'
            sliceCount={1}
            renderItem={work => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {work?.company}
                </Typography>
                <Typography variant='body2'>{` Postion: ${work?.position} `}</Typography>
                <Typography variant='body2'>{`Duration: ${formatDateString(work.startDate)} to ${formatDateString(
                  work.endDate
                )}`}</Typography>
              </Box>
            )}
            handleShowAllToggle={handleShowAllToggle}
          />
        </Grid>
      </Box>
    </div>
  );
};

export default Profile;
